import { Injectable } from '@angular/core';
import Dexie, { Table } from 'dexie';
import { CompanyInfo, User } from '../models/company';
import { BoundBook, BoundBookEntry, Firearm, FFL, Form4473, Form4473Transferee, ReservedFirearm } from '../models/general';
import { Customer, ProductServiceItem, SalesReceipt, Expense, Vendor } from '../models/accounting';
import { AtfFfl } from '../models/atf-ffl';

export interface CachedCredential {
  email: string;
  passwordHash: string;
  user_id: string;
  company_id: string;
  username: string;
  role: string;
  name_first?: string;
  name_last?: string;
}

export interface CalendarEventEntry {
  id?: string;
  company_id: string;
  title: string;
  start: string;
  end: string;
  customer_id?: string;
  description?: string;
  send_reminder?: boolean;
  created_by?: string;
  updatedAt?: string;
}


export interface SyncOperation {
  id?: string;
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  url: string;
  payload?: any;
  /** Client-generated temp ID stored here so the sync service can rewrite
   *  subsequent queue entries after the server assigns a real ID on POST. */
  tempId?: string;
  /** When true, the sync service must call GET /api/bound_books/max-d-form4473
   *  and assign the next transaction_serial_number before sending the POST.
   *  Ensures chronological order because ops are drained oldest-first. */
  needsTransactionId?: boolean;
  timestamp: number;
  retryCount: number;
}

export interface SyncMetadata {
  id: string;
  company_id: string;
  lastSyncedAt: string;
}

export interface CachedCompanyDocument {
  /** `${company_id}_${document_type}` */
  id: string;
  company_id: string;
  document_type: 'ffl' | 'sot';
  document_data: string;
  updatedAt: string;
  file_name?: string | null;
  file_size_kb?: number | null;
}

@Injectable({
  providedIn: 'root'
})
export class LocalDatabaseService extends Dexie {
  companyProfiles!: Table<CompanyInfo, string>;
  users!: Table<User, string>;
  boundBooks!: Table<BoundBook, string>;
  firearms!: Table<Firearm, string>;
  ffls!: Table<FFL, string>;
  vendors!: Table<Vendor, string>;
  customers!: Table<Customer, string>;
  productsServices!: Table<ProductServiceItem, string>;
  salesReceipts!: Table<SalesReceipt, string>;
  expenseRecords!: Table<Expense, string>;
  form4473s!: Table<Form4473, string>;
  reservedFirearms!: Table<ReservedFirearm, string>;
  reserved4473s!: Table<Form4473Transferee, string>;
  syncQueue!: Table<SyncOperation, string>;
  calendarEvents!: Table<CalendarEventEntry, string>;
  cachedCredentials!: Table<CachedCredential, string>;
  syncMeta!: Table<SyncMetadata, string>;
  companyDocuments!: Table<CachedCompanyDocument, string>;
  atfFflList!: Table<AtfFfl, string>;


  constructor() {
    super('KhaosOfflineDB');
    
    // Define the schema for the tables.
    // The keys specified here are only the ones that we will index on.
    this.version(1).stores({
      companyProfiles: 'company_id',
      users: 'user_id, company_id',
      boundBooks: '_id, company_id', 
      firearms: 'id, company_id',
      ffls: 'id, company_id',
      syncQueue: 'id, timestamp'
    });

    this.version(2).stores({
      vendors: 'id, company_id',
    });

    this.version(3).stores({
      customers: 'id, company_id',
      productsServices: 'id, company_id',
    });

    this.version(4).stores({
      salesReceipts: 'id, company_id',
      expenseRecords: 'id, company_id',
    });

    this.version(5).stores({
      form4473s: '_id, company_id',
      reservedFirearms: '_id, company_id, bound_book_id',
    });

    this.version(6).stores({
      reserved4473s: 'id, company_id',
    });

    this.version(7).stores({
      companyProfiles: 'company_id',
      users: 'user_id, company_id',
      boundBooks: '_id, company_id',
      firearms: 'id, company_id',
      ffls: 'id, company_id',
      vendors: 'id, company_id',
      customers: 'id, company_id',
      productsServices: 'id, company_id',
      salesReceipts: 'id, company_id',
      expenseRecords: 'id, company_id',
      form4473s: '_id, company_id',
      reservedFirearms: '_id, company_id, bound_book_id',
      reserved4473s: 'id, company_id',
      syncQueue: 'id, timestamp, url'
    });

    this.version(8).stores({
      calendarEvents: 'id, company_id',
    });

    this.version(9).stores({
      cachedCredentials: 'email',
    });

    this.version(10).stores({
      companyProfiles: 'company_id',
      users: 'user_id, company_id',
      boundBooks: '_id, company_id, updatedAt',
      firearms: 'id, company_id, updatedAt',
      ffls: 'id, company_id, updatedAt',
      vendors: 'id, company_id, updatedAt',
      customers: 'id, company_id, updatedAt',
      productsServices: 'id, company_id, updatedAt',
      salesReceipts: 'id, company_id, updatedAt',
      expenseRecords: 'id, company_id, updatedAt',
      form4473s: '_id, company_id, updatedAt',
      reservedFirearms: '_id, company_id, bound_book_id, updatedAt',
      reserved4473s: 'id, company_id, updatedAt',
      calendarEvents: 'id, company_id, updatedAt',
      syncQueue: 'id, timestamp, url',
      cachedCredentials: 'email',
      syncMeta: 'id, company_id',
    });

    this.version(11).stores({
      companyDocuments: 'id, company_id',
    });

    // atf_ffl_lists is global reference data (not company-scoped) synced via
    // GET /api/atf_ffl_lists/sync — see AtfFflSyncService. Keyed by
    // ffl_number, the collection's natural/unique key, rather than a
    // synthetic id.
    this.version(12).stores({
      atfFflList: 'ffl_number, updated_at',
    });
  }
}
